import { FolderOpen, X } from 'lucide-react';
import React from 'react';
import { useGetProjectsQuery } from '../../services/projectApi';

interface CalendarProjectFilterProps {
  selectedProjectIds: string[];
  onChange: (ids: string[]) => void;
}

const CalendarProjectFilter: React.FC<CalendarProjectFilterProps> = ({
  selectedProjectIds,
  onChange,
}) => {
  const { data: projectsData } = useGetProjectsQuery({ limit: 100 });
  const projects = projectsData?.data?.projects || [];

  const toggleProject = (id: string) => {
    if (selectedProjectIds.includes(id)) {
      onChange(selectedProjectIds.filter(pid => pid !== id));
    } else {
      onChange([...selectedProjectIds, id]);
    }
  };

  const clearAll = () => onChange([]);

  if (projects.length === 0) return null;

  return (
    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
      <div className="flex items-center space-x-1 text-xs text-gray-500 mr-1">
        <FolderOpen className="h-3.5 w-3.5" />
        <span>Projects:</span>
      </div>

      {selectedProjectIds.length === 0 ? (
        <span className="text-xs text-gray-400">All projects</span>
      ) : (
        <>
          {selectedProjectIds.map(id => {
            const project = projects.find(p => p._id === id);
            return (
              <button
                key={id}
                onClick={() => toggleProject(id)}
                className="inline-flex items-center space-x-1 px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full text-xs hover:bg-brand-100 transition-colors"
              >
                <span className="max-w-[120px] truncate">{project?.name || id}</span>
                <X className="h-3 w-3" />
              </button>
            );
          })}
          <button
            onClick={clearAll}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Clear
          </button>
        </>
      )}

      {/* Project selection dropdown trigger */}
      <div className="relative group">
        <button className="text-xs text-brand-primary hover:text-brand-700 transition-colors px-1">
          + Filter
        </button>
        <div className="hidden group-hover:block absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {projects.map(project => (
            <button
              key={project._id}
              onClick={() => toggleProject(project._id)}
              className={`flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                selectedProjectIds.includes(project._id)
                  ? 'text-brand-primary font-medium bg-brand-50'
                  : 'text-gray-700'
              }`}
            >
              <div
                className={`w-3 h-3 rounded border mr-2 flex items-center justify-center ${
                  selectedProjectIds.includes(project._id)
                    ? 'bg-brand-primary border-brand-primary'
                    : 'border-gray-300'
                }`}
              >
                {selectedProjectIds.includes(project._id) && (
                  <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                )}
              </div>
              <span className="truncate">{project.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarProjectFilter;
